import {
  ColumnTable,
  OutlinedSelect,
  SelectOptionType,
} from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import { TranslationsKeys } from '../../../../../../locales/translations';
import {
  CertificateType,
  PetitionPaymentTypeId,
  PetitionType,
  PetitionTypeId,
} from '../types';

/**
 * Columnas de las tablas de precios (certificados y solicitudes).
 *
 * Los textos siguen leyéndose del namespace de Configuración > Financieros -
 * otros, de donde vienen estas secciones: renombrar las claves rompería los
 * overrides por tenant que ya apuntan ahí.
 */
export function usePriceColumns({
  onCheck,
  onChangeValue,
  paymentTypeOptions = [],
  showPaymentType = false,
  translateKey,
}: {
  onCheck: (typeId: number | PetitionTypeId, value: boolean) => void;
  onChangeValue: (
    typeId: number | PetitionTypeId,
    fieldName: string,
    fieldValue: number | string,
  ) => void;
  paymentTypeOptions?: SelectOptionType[];
  showPaymentType?: boolean;
  translateKey:
    | 'petitionTypes'
    | 'certificateTypes'
    | 'academicLevelTitulationPayment';
}) {
  const { t } = useTranslation(
    TranslationsKeys.CONFIGURATION_FIANANTIAL_OTHERS,
  );

  return useMemo<ColumnTable<PetitionType | CertificateType>[]>(
    () => [
      {
        columnName: 'name',
        headerText: t(`${translateKey}.table.name`),
        tdClassName: 'text-left',
        thClassName: 'text-left',
        cellFormat: (options) => (
          <>
            {translateKey && (
              <span>
                {translateKey === 'academicLevelTitulationPayment' &&
                  t(`${translateKey}.table.rowPrefix`)}
                {options.row.name}
              </span>
            )}
          </>
        ),
      },
      {
        columnName: 'paymentRequired',
        headerText: t(`${translateKey}.table.paymentRequirement`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        width: '20%',
        cellFormat: (options) => {
          return (
            <div className="form-group" style={{ marginTop: '-15px' }}>
              <label className="g-checkbox-input-container">
                <Input
                  className="g-checkbox-input"
                  type="checkbox"
                  defaultChecked={Boolean(options.row.paymentRequired)}
                  onChange={() => {
                    onCheck(
                      options.row.id,
                      !Boolean(options.row.paymentRequired),
                    );
                  }}
                />
                <span className="checkmark"></span>
              </label>
              <small className="g-input-error form-text text-danger"></small>
            </div>
          );
        },
      },
      ...(showPaymentType
        ? [
            {
              columnName: 'paymentType',
              headerText: t(`${translateKey}.table.paymentType`),
              tdClassName: 'text-center',
              thClassName: 'text-center',
              width: '20%',
              cellFormat: (options: any) => {
                if (!options.row.paymentRequired) {
                  return <></>;
                }

                const defaultValue = paymentTypeOptions.find(
                  (option) =>
                    option.value ===
                    (options.value ?? PetitionPaymentTypeId.BY_FIXED),
                );
                return (
                  <div className="form-group">
                    <OutlinedSelect
                      name="paymentType"
                      label={''}
                      options={paymentTypeOptions}
                      value={defaultValue}
                      onChange={(e) => {
                        onChangeValue(options.row.id, 'paymentType', e.value);
                      }}
                      isClearable={false}
                      height="100%"
                    />
                  </div>
                );
              },
            },
          ]
        : []),
      {
        columnName: 'paymentAmount',
        headerText: t(`${translateKey}.table.paymentAmount`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        width: '20%',
        cellFormat: (options) => {
          const isDisabled =
            !Boolean(options.row.paymentRequired) ||
            ('paymentType' in options.row &&
              options.row.paymentType === PetitionPaymentTypeId.BY_CREDIT);

          const isValueInvalid =
            Boolean(options.row.paymentRequired) &&
            (options.value < 1 ||
              options.value === null ||
              options.value === '');

          const isInvalid =
            'paymentType' in options.row &&
            options.row.paymentType !== PetitionPaymentTypeId.BY_FIXED
              ? false
              : isValueInvalid;

          return (
            <div className="form-group" style={{ position: 'relative' }}>
              <Input
                style={{ height: '38px', textAlign: 'center' }}
                name="paymentAmount"
                className="border-primary"
                type="number"
                min={1}
                required={Boolean(options.row.paymentRequired) && !isDisabled}
                invalid={isInvalid}
                disabled={isDisabled}
                defaultValue={options.value}
                onChange={(e) => {
                  onChangeValue(options.row.id, e.target.name, e.target.value);
                }}
              />
              {isInvalid && (
                <small
                  className="text-danger d-block mt-1"
                  style={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t(`onEmptyValue`)}
                </small>
              )}
            </div>
          );
        },
      },
    ],
    [
      onCheck,
      onChangeValue,
      t,
      translateKey,
      paymentTypeOptions,
      showPaymentType,
    ],
  );
}

export default usePriceColumns;
